/* FormulaVersionBadge — малка табелка показваща текущите версии на NIS/DIL.
 *
 * Цели:
 *   • EU AI Act Art. 50 transparency: clinicians/researchers ясно виждат
 *     коя версия на формулата работи в момента.
 *   • Hover/tap → tooltip с пълни OOS метрики и линк към open-science page.
 *
 * Mount в clinical-grade модули:
 *   • PlateSimulator (per-meal NIS%)
 *   • DigitalTwinPanel (forecasts)
 *   • MealLog (daily DIL totals)
 *
 * Стилово: подобно на legal-disclaimer текст — малък, ненатрапчив, footer-level.
 */
import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Link } from "react-router-dom";

const NIS_VERSION = "v1.1.9";
const DIL_VERSION = "v2.0";

export default function FormulaVersionBadge({ variant = "inline", showLink = true }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [showTooltip, setShowTooltip] = useState(false);

  const tooltipText = bg
    ? `NIS ${NIS_VERSION}: peer-reviewed формула с фиксирани коефициенти (n=147 cohort). OOS метрики (n=25): MAE=10.2 [7.06, 13.70], Pearson r=0.826. DIL ${DIL_VERSION}: 0.69 × протеин + max(въглехидрати − фибри, 0). Apache 2.0 open-source SDK.`
    : `NIS ${NIS_VERSION}: peer-reviewed formula with frozen coefficients (n=147 cohort). OOS metrics (n=25): MAE=10.2 [7.06, 13.70], Pearson r=0.826. DIL ${DIL_VERSION}: 0.69 × protein + max(carbs − fiber, 0). Apache 2.0 open-source SDK.`;

  const baseCls = `inline-flex items-center gap-1.5 text-[10px] font-medium select-none ${
    isDark ? "text-slate-500" : "text-slate-400"
  }`;

  const content = (
    <>
      <span className={`px-1.5 py-0.5 rounded ${isDark ? "bg-slate-800/60" : "bg-slate-100"}`}>
        NIS {NIS_VERSION}
      </span>
      <span className={isDark ? "text-slate-600" : "text-slate-300"}>·</span>
      <span className={`px-1.5 py-0.5 rounded ${isDark ? "bg-slate-800/60" : "bg-slate-100"}`}>
        DIL {DIL_VERSION}
      </span>
    </>
  );

  // iter540 — Inline PyPI + Zenodo DOI чипове до version badge-а.
  // Click → отварят външните citable източници в нов tab.
  const extLinks = (
    <span className="inline-flex items-center gap-1 ml-1.5">
      <span className={isDark ? "text-slate-600" : "text-slate-300"}>·</span>
      <a
        href="https://pypi.org/project/nutramilo-nis/"
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
          isDark
            ? "bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25"
            : "bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
        }`}
        data-testid="formula-version-pypi-link"
        title="PyPI"
      >
        PyPI ↗
      </a>
      <a
        href="https://doi.org/10.5281/zenodo.20571802"
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
          isDark
            ? "bg-violet-500/15 text-violet-300 hover:bg-violet-500/25"
            : "bg-violet-50 text-violet-700 hover:bg-violet-100"
        }`}
        data-testid="formula-version-zenodo-link"
        title="Zenodo DOI 10.5281/zenodo.20571802"
      >
        DOI ↗
      </a>
    </span>
  );

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      data-testid="formula-version-badge"
    >
      {showLink ? (
        <Link
          to="/scientific-formulas"
          className={`${baseCls} hover:underline cursor-pointer transition-colors ${
            isDark ? "hover:text-slate-300" : "hover:text-slate-600"
          }`}
          aria-label={tooltipText}
        >
          {content}
        </Link>
      ) : (
        <span className={baseCls} aria-label={tooltipText}>{content}</span>
      )}
      {extLinks}

      {showTooltip && (
        <div
          role="tooltip"
          className={`absolute bottom-full left-0 mb-1.5 w-72 z-50 px-3 py-2 rounded-lg text-[11px] font-medium shadow-lg pointer-events-none leading-relaxed
            ${isDark ? "bg-gray-900 text-gray-100 border border-white/10"
                    : "bg-white text-gray-700 border border-gray-200"}`}
          data-testid="formula-version-badge-tooltip"
        >
          {tooltipText}
        </div>
      )}
    </div>
  );
}
